import { decode, encode } from 'base64-arraybuffer';
import { createStore, del, get, set } from 'idb-keyval';
const subtle = crypto.subtle;
const textEncoder = new TextEncoder();
const LOCAL_KEY_NAME = 'local-key';
const store = createStore('thing-rtc', 'local-key-pairs');
/**
 * Keypair representing the identity of our local client.
 */
export class LocalKeyPair {
    keyPair;
    publicKeyDigest;
    publicKeySpki;
    /**
     * Generate a new public/private keypair for local use.
     */
    static async createLocalKeyPair() {
        return await LocalKeyPair.createFromKeyPair(await generateKeyPair());
    }
    /**
     * Load the saved local public/private keypair from storage.
     */
    static async loadLocalKeyPair() {
        const cryptoKeyPair = await get(LOCAL_KEY_NAME, store);
        if (cryptoKeyPair) {
            return await LocalKeyPair.createFromKeyPair(cryptoKeyPair);
        }
        else {
            return null;
        }
    }
    /**
     * Clear the local public/private keypair storage.
     */
    static async clearLocalKeyPair() {
        await del(LOCAL_KEY_NAME, store);
    }
    static async createFromKeyPair(keyPair) {
        const digest = await publicKeyDigest(keyPair.publicKey);
        const spki = await exportPublicKey(keyPair.publicKey);
        return new LocalKeyPair(keyPair, digest, spki);
    }
    constructor(keyPair, publicKeyDigest, publicKeySpki) {
        this.keyPair = keyPair;
        this.publicKeyDigest = publicKeyDigest;
        this.publicKeySpki = publicKeySpki;
    }
    async sign(message) {
        return await subtle.sign({
            name: 'ECDSA',
            hash: 'SHA-256'
        }, this.keyPair.privateKey, message);
    }
    /**
     * Save this key pair to storage, overwriting any existing keypair.
     */
    async save() {
        await set(LOCAL_KEY_NAME, this.keyPair, store);
    }
}
/**
 * Public key representing the identity of a trusted remote peer.
 */
export class RemoteKey {
    publicKey;
    publicKeyDigest;
    publicKeySpki;
    /**
     * Create a RemoteKey representation of the provided SPKI, base64-encoded
     * public key.
     */
    static async createRemoteKey(spki) {
        const publicKey = await importPublicKey(spki);
        const digest = await publicKeyDigest(publicKey);
        return new RemoteKey(publicKey, digest, spki);
    }
    constructor(publicKey, publicKeyDigest, publicKeySpki) {
        this.publicKey = publicKey;
        this.publicKeyDigest = publicKeyDigest;
        this.publicKeySpki = publicKeySpki;
    }
    async verify(signature, message) {
        return await subtle.verify({
            name: 'ECDSA',
            hash: 'SHA-256'
        }, this.publicKey, signature, message);
    }
}
export var KeyPair;
(function (KeyPair) {
    /**
     * Creates the PeerConfig representing a connection between the local client
     * and a provided remote peer.
     */
    async function createConfig(remotePublicKey, localKeyPair, role) {
        const peerAuth = new WebCryptoPeerAuth(remotePublicKey, localKeyPair);
        const pairingId = await toPairingId(remotePublicKey, localKeyPair);
        return {
            pairingId,
            peerAuth,
            role,
        };
    }
    KeyPair.createConfig = createConfig;
})(KeyPair || (KeyPair = {}));
export class WebCryptoPeerAuth {
    remotePublicKey;
    localKeyPair;
    constructor(remotePublicKey, localKeyPair) {
        this.remotePublicKey = remotePublicKey;
        this.localKeyPair = localKeyPair;
    }
    async signMessage(message) {
        return encode(await this.localKeyPair.sign(textEncoder.encode(message).buffer));
    }
    async verifyMessage(base64Signature, message) {
        const messageBuffer = textEncoder.encode(message);
        return await this.remotePublicKey.verify(decode(base64Signature), messageBuffer.buffer);
    }
}
async function generateKeyPair() {
    return await subtle.generateKey({
        name: 'ECDSA',
        namedCurve: 'P-256'
    }, false, ['sign', 'verify']);
}
/**
 * Imports a base64-encoded SPKI public key into a web CryptoKey object.
 */
async function importPublicKey(spki) {
    const algorithm = {
        name: 'ECDSA',
        namedCurve: 'P-256'
    };
    return await subtle.importKey('spki', decode(spki), algorithm, true, ['verify']);
}
/**
 * Exports a SPKI public key from a web CryptoKey object.
 */
async function exportPublicKey(key) {
    return encode(await subtle.exportKey('spki', key));
}
async function publicKeyDigest(key) {
    const spki = await subtle.exportKey('spki', key);
    const hash = await subtle.digest('SHA-256', spki);
    return encode(hash);
}
async function toPairingId(remotePublicKey, localKeyPair) {
    // For public keys pairing IDs, we use the concatenated, sorted public key
    // fingerprints, separated by a ':' which is not present in the base64
    // fingerprints.
    const fingerprints = [
        remotePublicKey.publicKeyDigest,
        localKeyPair.publicKeyDigest
    ];
    return fingerprints.toSorted().join(':');
}
